import { Fragment } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { BadgeButton } from '@components/badge-button';
import { IconForm } from '@components/icons/form';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconPortal } from '@components/icons/portal';
import { ListCard } from '@components/list-card';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Horizontal, Stack } from '@components/stack';
import { Text } from '@components/text';
import { ValueList } from '@components/value-list';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { config } from 'app-config';

import { useCurrentCompany } from 'api/authentication/hooks';
import { JourneyListJourneyForm, JourneyType } from 'api/types';
import { routePaths } from 'ui/authenticated/navigation';

import { JourneyMenu } from './common';

type PortalGroup = {
  portal_name: string;
  portal_shortId: string;
  portal_slug: string;
  journey_forms: JourneyListJourneyForm[];
};

type JourneyListItemProps = {
  journey: JourneyType;
  hideMenu?: boolean;
  hideActions?: boolean;
};

export const JourneyListItem = ({
  journey,
  hideMenu = false,
  hideActions = false,
}: JourneyListItemProps) => {
  const { t } = useScopedTranslate(journeyCardLocale);
  const company = useCurrentCompany();
  const navigate = useNavigate();
  const path = generatePath(routePaths.journeyDetails, {
    workspaceId: journey.workspace,
    journeyId: journey.shortId,
    '*': '',
  });

  const portalDomainBase = company?.portalDomain
    ? `https://${company.portalDomain}`
    : `${config.PORTAL_URL}/${company?.slug}/`;

  const portalForms = journey.journeyForms?.filter((form) => form.isPortalForm);

  const hasjourneyForms = (journey.journeyForms?.length ?? 0) > 0;
  const hasPortalForms = (portalForms?.length ?? 0) > 0;
  const portalGroups: PortalGroup[] =
    portalForms?.reduce((acc, form) => {
      form?.portals?.forEach((portal) => {
        const existingGroup = acc.find((group) => group.portal_shortId === portal.shortId);
        if (existingGroup) {
          existingGroup.journey_forms.push(form);
        } else {
          acc.push({
            portal_name: portal.name,
            portal_shortId: portal.shortId,
            portal_slug: portal.slug,
            journey_forms: [form],
          });
        }
      });
      return acc;
    }, [] as PortalGroup[]) ?? [];

  return (
    <ListCard
      to={path}
      title={journey.title}
      description={journey.description}
      icon={<IconJourneyItem />}
      actions={
        !hideActions && (
          <Horizontal align="center" space={4}>
            {hasjourneyForms && (
              <>
                <Stack space={1}>
                  <Menu
                    dataTestid="active-forms"
                    placement="bottom-start"
                    trigger={BadgeButton}
                    triggerProps={{
                      icon: IconForm,
                      text: journey.journeyForms?.length.toString() ?? '0',
                    }}
                  >
                    <Stack space={4}>
                      <Text text={t('activeForms')} font="semibold14" />
                      <div>
                        {journey.journeyForms?.map((form) => {
                          const journeyFormPath =
                            generatePath(routePaths.editJourney, {
                              workspaceId: journey.workspace,
                              journeyId: journey.shortId,
                              '*': 'journey',
                            }) + `?openJourneyForm=${form.shortId}`;
                          return (
                            <Menu.Item
                              key={journeyFormPath}
                              icon={<IconForm />}
                              label={form.name}
                              onSelect={() => navigate(journeyFormPath)}
                            />
                          );
                        })}
                      </div>
                    </Stack>
                  </Menu>
                  {hasPortalForms && (
                    <Menu
                      placement="bottom-start"
                      dataTestid="visible-on-portal-forms"
                      trigger={BadgeButton}
                      triggerProps={{
                        icon: IconPortal,
                        text: portalForms?.length.toString() ?? '0',
                      }}
                    >
                      <Stack style={{ padding: `${pxToRem(8)} 0` }}>
                        <Text text={t('visibleOnPortal')} font="semibold14" />
                        <Menu.Separator />
                        {portalGroups.map((group, groupIndex) => (
                          <Fragment key={group.portal_shortId}>
                            <Stack>
                              <Text text={group.portal_name} font="semibold14" />
                              {group.journey_forms.map((form) => (
                                <Menu.ItemLink
                                  key={form.shortId}
                                  icon={<IconForm color="elementPurple80" />}
                                  label={
                                    <Stack orientation="horizontal" space={1} align="center">
                                      <Text text={form.name} />
                                      <IconOpenNewTab size={16} color="elementPurple50" />
                                    </Stack>
                                  }
                                  to={`${portalDomainBase}${group.portal_slug}/forms/${form.uuid}`}
                                  isExternal
                                />
                              ))}
                            </Stack>
                            {groupIndex < portalGroups.length - 1 && <Menu.Separator />}
                          </Fragment>
                        ))}
                      </Stack>
                    </Menu>
                  )}
                </Stack>
                <Separator orientation="horizontal" style={{ maxHeight: pxToRem(36) }} />
              </>
            )}
            <ValueList
              items={[
                { value: journey.numNotStartedTravellers ?? 0, label: t('notstarted') },
                { value: journey.numInProgressTravellers ?? 0, label: t('inprogress') },
                { value: journey.numCompletedTravellers ?? 0, label: t('completed') },
                { value: journey.numTotalTravellers ?? 0, label: t('travellers') },
                { value: journey.numNodeActions ?? 0, label: t('actions') },
              ]}
            />
            {!hideMenu && <JourneyMenu journey={journey} variant="ghost" />}
          </Horizontal>
        )
      }
    />
  );
};

const journeyCardLocale = {
  en: {
    actions: 'Actions',
    notstarted: 'Not started',
    inprogress: 'In progress',
    completed: 'Completed',
    travellers: 'Travellers',
    visibleOnPortal: 'Visible on portal',
    activeForms: 'Active external forms',
  },
};
